/**
 * The door's offline queue.
 *
 * A door is the worst place on the platform to need a connection: a basement, a field, a venue
 * whose wifi dies at 9pm. When the network drops mid-shift the scanner must keep letting people
 * in, and reconcile when it comes back — the alternative is a host waving everyone through and
 * nobody knowing who actually came.
 *
 * The honest shape of that is: a scan made offline is PROVISIONAL. It admits the person at the
 * door (the host can see the queue and decide), it is stored, and it is replayed in order the
 * moment the connection returns. The server is still the authority — a replayed scan can come
 * back `already`, `invalid` or `refunded`, and the queue reports that rather than hiding it, so
 * a seat that was refunded while the door was dark is surfaced instead of silently honoured.
 *
 * What this deliberately does NOT do: decide entry offline. It cannot know a seat was refunded
 * two minutes ago. It records "this code was presented at 9:41" and says so plainly.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const KEY = (doorId) => `pr.doorQueue.${doorId}`;
/** A queued scan older than this is no longer worth replaying — the night is over. */
const MAX_AGE_MS = 12 * 60 * 60 * 1000;

const read = (doorId) => {
  try {
    const raw = window.localStorage.getItem(KEY(doorId));
    if (!raw) return [];
    const list = JSON.parse(raw);
    if (!Array.isArray(list)) return [];
    return list.filter((q) => q && q.code && Date.now() - (q.at || 0) < MAX_AGE_MS);
  } catch { return []; }
};
const write = (doorId, list) => {
  try { window.localStorage.setItem(KEY(doorId), JSON.stringify(list)); } catch { /* no storage */ }
};

/** Offline when the browser says so. A failed request also parks the scan — see `enqueue`. */
export const isOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false;

/**
 * @param doorId  stable per door (the event or gathering id) so two doors never share a queue
 * @param send    (code) => the same shape the scanner's api.scan returns
 * @param onResolved (code, result) => void, once the server has finally ruled on a queued scan
 */
export default function useDoorQueue({ doorId, send, onResolved }) {
  const [queued, setQueued] = useState(() => (doorId ? read(doorId) : []));
  const flushing = useRef(false);
  const sendRef = useRef(send);
  const resolvedRef = useRef(onResolved);
  useEffect(() => { sendRef.current = send; }, [send]);
  useEffect(() => { resolvedRef.current = onResolved; }, [onResolved]);

  const persist = useCallback((list) => {
    setQueued(list);
    if (doorId) write(doorId, list);
  }, [doorId]);

  /** Park a scan the network couldn't deliver. Idempotent per code: scanning the same QR twice
   *  while offline queues it once, so the replay can't double-count it. */
  const enqueue = useCallback((code) => {
    if (!doorId) return;
    const list = read(doorId);
    if (list.some((q) => q.code === code)) return;
    persist([...list, { code, at: Date.now() }]);
  }, [doorId, persist]);

  /** Replay everything, oldest first, stopping the moment the network fails again. */
  const flush = useCallback(async () => {
    if (!doorId || flushing.current || isOffline()) return 0;
    const list = read(doorId);
    if (list.length === 0) return 0;
    flushing.current = true;
    let remaining = [...list];
    let settled = 0;
    try {
      for (const item of list) {
        let r;
        // eslint-disable-next-line no-await-in-loop
        try { r = await sendRef.current(item.code); } catch { break; } // still down — keep the rest
        if (!r?.success) break;
        remaining = remaining.filter((q) => q.code !== item.code);
        persist(remaining);
        settled += 1;
        resolvedRef.current?.(item.code, r.data);
      }
    } finally {
      flushing.current = false;
    }
    return settled;
  }, [doorId, persist]);

  // Come back online → replay. Also try once on mount, for the shift that reopens the page.
  useEffect(() => {
    if (!doorId) return undefined;
    const onOnline = () => { flush(); };
    window.addEventListener('online', onOnline);
    flush();
    return () => window.removeEventListener('online', onOnline);
  }, [doorId, flush]);

  const clear = useCallback(() => persist([]), [persist]);

  return { queued, enqueue, flush, clear, offline: isOffline() };
}
